import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// Declare Clover SDK types
declare global {
    interface Window {
        Clover: any;
    }
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CLOVER_PAKMS_KEY = process.env.REACT_APP_CLOVER_PAKMS_KEY || 'dummy_pakms_key_replace_with_real';

interface Tournament {
    CLAVE: number;
    NOMBRE: string;
    FECHAINICIO: string;
    FECHAFIN: string;
    CVEBOLICHE: number;
}

interface BalanceData {
    COMPRADO: number;
    PAGADO: number;
    BALANCE: number;
}

const PlayerFinances: React.FC = () => {
    const navigate = useNavigate();
    const [tournaments, setTournaments] = useState<Tournament[]>([]);
    const [selectedTournamentId, setSelectedTournamentId] = useState<number | null>(null);
    const [balance, setBalance] = useState<BalanceData | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadingBalance, setLoadingBalance] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Payment modal state
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [paymentAmount, setPaymentAmount] = useState('');
    const [isProcessingPayment, setIsProcessingPayment] = useState(false);
    const [paymentError, setPaymentError] = useState('');
    const [cloverLoaded, setCloverLoaded] = useState(false);
    const cloverRef = useRef<any>(null);

    useEffect(() => {
        loadTournaments();
        
        // Load Clover SDK script
        if (!document.getElementById('clover-sdk-script')) {
            const script = document.createElement('script');
            script.id = 'clover-sdk-script';
            script.src = 'https://checkout.clover.com/sdk.js';
            script.async = true;
            document.body.appendChild(script);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (selectedTournamentId) {
            loadBalance();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTournamentId]);

    const loadTournaments = async () => {
        try {
            setLoading(true);
            const loggedInPlayerStr = localStorage.getItem('loggedInPlayer');
            if (!loggedInPlayerStr) {
                navigate('/login');
                return;
            }

            const loggedInPlayer = JSON.parse(loggedInPlayerStr);
            const response = await fetch(`${BACKEND_URL}/api/finances/tournaments/${loggedInPlayer.CLAVE}`);

            if (!response.ok) {
                throw new Error('Failed to load tournaments');
            }

            const data = await response.json();
            setTournaments(data);

            // Auto-select first tournament if available
            if (data.length > 0) {
                setSelectedTournamentId(data[0].CLAVE);
            }
        } catch (err: any) {
            console.error('Error loading tournaments:', err);
            setError(err.message || 'Failed to load tournaments');
        } finally {
            setLoading(false);
        }
    };

    const loadBalance = async () => {
        if (!selectedTournamentId) return;

        try {
            setLoadingBalance(true);
            setError(null);
            const loggedInPlayerStr = localStorage.getItem('loggedInPlayer');
            if (!loggedInPlayerStr) {
                navigate('/login');
                return;
            }

            const loggedInPlayer = JSON.parse(loggedInPlayerStr);
            const response = await fetch(
                `${BACKEND_URL}/api/finances/balance/${selectedTournamentId}/${loggedInPlayer.CLAVE}`
            );

            if (!response.ok) {
                throw new Error('Failed to load balance');
            }

            const data = await response.json();
            setBalance(data);
        } catch (err: any) {
            console.error('Error loading balance:', err);
            setError(err.message || 'Failed to load balance');
        } finally {
            setLoadingBalance(false);
        }
    };

    // Initialize Clover SDK when modal opens
    const initializeClover = () => {
        if (!window.Clover) {
            setPaymentError('Payment system not loaded. Please refresh the page.');
            return;
        }

        try {
            const clover = new window.Clover(CLOVER_PAKMS_KEY);
            cloverRef.current = clover;

            const elements = clover.elements();

            // Create and mount card number element
            const cardNumber = elements.create('CARD_NUMBER', {
                style: {
                    base: {
                        fontSize: '16px',
                        fontFamily: 'Arial, sans-serif',
                        color: '#333',
                        '::placeholder': { color: '#aaa' }
                    }
                }
            });
            cardNumber.mount('#card-number');

            // Create and mount card date element
            const cardDate = elements.create('CARD_DATE', {
                style: {
                    base: {
                        fontSize: '16px',
                        fontFamily: 'Arial, sans-serif',
                        color: '#333',
                        '::placeholder': { color: '#aaa' }
                    }
                }
            });
            cardDate.mount('#card-date');

            // Create and mount CVV element
            const cardCvv = elements.create('CARD_CVV', {
                style: {
                    base: {
                        fontSize: '16px',
                        fontFamily: 'Arial, sans-serif',
                        color: '#333',
                        '::placeholder': { color: '#aaa' }
                    }
                }
            });
            cardCvv.mount('#card-cvv');

            // Create and mount postal code element
            const cardPostalCode = elements.create('CARD_POSTAL_CODE', {
                style: {
                    base: {
                        fontSize: '16px',
                        fontFamily: 'Arial, sans-serif',
                        color: '#333',
                        '::placeholder': { color: '#aaa' }
                    }
                }
            });
            cardPostalCode.mount('#card-postal-code');

            setCloverLoaded(true);
        } catch (error) {
            console.error('Clover initialization error:', error);
            setPaymentError('Failed to initialize payment system');
        }
    };

    // Open payment modal
    const openPaymentModal = () => {
        if (balance && balance.BALANCE < 0) {
            setPaymentAmount(Math.abs(balance.BALANCE).toFixed(2));
        }
        setShowPaymentModal(true);
        setPaymentError('');
        
        // Initialize Clover after modal is visible
        setTimeout(() => {
            initializeClover();
        }, 100);
    };

    // Close payment modal and cleanup
    const closePaymentModal = () => {
        setShowPaymentModal(false);
        setPaymentAmount('');
        setPaymentError('');
        setCloverLoaded(false);
        
        // Cleanup Clover iframes
        const ids = ['card-number', 'card-date', 'card-cvv', 'card-postal-code'];
        ids.forEach(id => {
            const element = document.getElementById(id);
            if (element) element.innerHTML = '';
        });
        cloverRef.current = null;
    };

    // Handle payment submission
    const handlePayment = async () => {
        if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
            setPaymentError('Please enter a valid payment amount');
            return;
        }

        if (!cloverRef.current) {
            setPaymentError('Payment system not initialized');
            return;
        }

        setIsProcessingPayment(true);
        setPaymentError('');

        try {
            // Get Clover token
            const result = await cloverRef.current.createToken();
            
            if (result.errors) {
                throw new Error(result.errors[0]?.message || 'Failed to process card information');
            }

            const cloverToken = result.token;

            // Get player info
            const loggedInPlayerStr = localStorage.getItem('loggedInPlayer');
            if (!loggedInPlayerStr) {
                navigate('/login');
                return;
            }
            const loggedInPlayer = JSON.parse(loggedInPlayerStr);

            // Send payment to backend
            const response = await fetch(`${BACKEND_URL}/api/finances/make-payment`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    cloverToken,
                    tournamentClave: selectedTournamentId,
                    jugadorClave: loggedInPlayer.CLAVE,
                    paymentAmount: parseFloat(paymentAmount)
                })
            });

            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(data.error || 'Payment failed');
            }

            // Success! Close modal and reload balance
            closePaymentModal();
            await loadBalance();
            
            // Show success message (you could add a success modal here)
            const feeLabel = data.payment.isGermanTax
                ? `3% CC fee; German Sales Tax (19%) included in price`
                : `including $${data.payment.fee.toFixed(2)} processing fee`;
            alert(`Payment successful! Amount charged: $${data.payment.total.toFixed(2)} (${feeLabel})`);
        } catch (error: any) {
            console.error('Payment error:', error);
            setPaymentError(error.message || 'Payment failed. Please try again.');
        } finally {
            setIsProcessingPayment(false);
        }
    };

    const isGermanTournament = tournaments.find(t => t.CLAVE === selectedTournamentId)?.CVEBOLICHE === 17;

    // Calculate fee in real-time
    const calculateFee = () => {
        const amount = parseFloat(paymentAmount);
        if (isNaN(amount) || amount <= 0) return 0;
        return +(amount * (isGermanTournament ? 0.03 : 0.05)).toFixed(2);
    };

    const calculateTotal = () => {
        const amount = parseFloat(paymentAmount);
        if (isNaN(amount) || amount <= 0) return 0;
        return +(amount * (isGermanTournament ? 1.03 : 1.05)).toFixed(2);
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
            {/* Header */}
            <div className="bg-white shadow-2xl border-b-4 border-gray-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="flex flex-col gap-6">
                        {/* Title Row with Back Button */}
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => navigate('/dashboard')}
                                className="flex-shrink-0 bg-gray-100 hover:bg-gray-200 p-3 rounded-xl transition-all"
                                aria-label="Back to dashboard"
                            >
                                <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                            <div className="bg-gray-50 rounded-2xl p-3 shadow-inner">
                                <img 
                                    src="/logo.avif" 
                                    alt="Logo" 
                                    className="w-12 h-12 sm:w-16 sm:h-16 object-contain"
                                />
                            </div>
                            <div className="flex-1 min-w-0">
                                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">My Tournament Balances</h1>
                                <p className="text-gray-600 text-sm sm:text-base">View your tournament balances and payments</p>
                            </div>
                        </div>

                        {/* Tournament Selector */}
                        {tournaments.length > 0 && (
                            <div>
                                <label className="block text-gray-700 text-sm font-semibold mb-2">
                                    Select Tournament
                                </label>
                                <select
                                    value={selectedTournamentId || ''}
                                    onChange={(e) => setSelectedTournamentId(Number(e.target.value))}
                                    className="w-full px-4 py-3 rounded-xl font-semibold shadow-lg border-2 border-gray-300 bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-all"
                                    disabled={loading}
                                >
                                    {tournaments.map((tournament) => (
                                        <option key={tournament.CLAVE} value={tournament.CLAVE}>
                                            {tournament.NOMBRE}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
                            <p className="text-gray-600 text-lg">Loading tournaments...</p>
                        </div>
                    </div>
                ) : tournaments.length === 0 ? (
                    <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
                        <svg className="w-20 h-20 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                        </svg>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">No Tournaments Found</h2>
                        <p className="text-gray-600 mb-6">You haven't registered for any tournaments yet</p>
                        <button
                            onClick={() => navigate('/welcome', { state: { fromDashboard: true } })}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-bold transition"
                        >
                            Register for a Tournament
                        </button>
                    </div>
                ) : error ? (
                    <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
                        <svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-red-600 text-lg font-semibold mb-2">Error Loading Balance</p>
                        <p className="text-gray-600">{error}</p>
                    </div>
                ) : loadingBalance ? (
                    <div className="flex items-center justify-center py-20">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
                            <p className="text-gray-600 text-lg">Loading balance...</p>
                        </div>
                    </div>
                ) : balance ? (
                    <div className="space-y-6">
                        {/* Balance Cards - Mobile First */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {/* Purchased Card */}
                            <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-blue-500">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Purchased</h3>
                                    <div className="bg-blue-50 rounded-lg p-2">
                                        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                                        </svg>
                                    </div>
                                </div>
                                <p className="text-3xl sm:text-4xl font-bold text-gray-900">{formatCurrency(balance.COMPRADO)}</p>
                                <p className="text-sm text-gray-500 mt-2">Total items purchased</p>
                            </div>

                            {/* Paid Card */}
                            <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-green-500">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Paid</h3>
                                    <div className="bg-green-50 rounded-lg p-2">
                                        <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                </div>
                                <p className="text-3xl sm:text-4xl font-bold text-gray-900">{formatCurrency(balance.PAGADO)}</p>
                                <p className="text-sm text-gray-500 mt-2">Total amount paid</p>
                            </div>

                            {/* Balance Card */}
                            <div className={`bg-white rounded-2xl shadow-lg p-6 border-l-4 ${
                                balance.BALANCE < 0 ? 'border-red-500' : balance.BALANCE > 0 ? 'border-amber-500' : 'border-gray-300'
                            }`}>
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Balance</h3>
                                    <div className={`rounded-lg p-2 ${
                                        balance.BALANCE < 0 ? 'bg-red-50' : balance.BALANCE > 0 ? 'bg-amber-50' : 'bg-gray-50'
                                    }`}>
                                        <svg className={`w-6 h-6 ${
                                            balance.BALANCE < 0 ? 'text-red-600' : balance.BALANCE > 0 ? 'text-amber-600' : 'text-gray-600'
                                        }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                </div>
                                <p className={`text-3xl sm:text-4xl font-bold ${
                                    balance.BALANCE < 0 ? 'text-red-600' : balance.BALANCE > 0 ? 'text-amber-600' : 'text-gray-900'
                                }`}>
                                    {formatCurrency(Math.abs(balance.BALANCE))}
                                </p>
                                <p className="text-sm text-gray-500 mt-2">
                                    {balance.BALANCE < 0 ? 'Amount owed' : balance.BALANCE > 0 ? 'Credit balance' : 'Fully paid'}
                                </p>
                            </div>
                        </div>

                        {/* Status Message */}
                        {balance.BALANCE !== 0 && (
                            <div className={`rounded-2xl shadow-lg p-6 ${
                                balance.BALANCE < 0 
                                    ? 'bg-red-50 border-2 border-red-200' 
                                    : 'bg-amber-50 border-2 border-amber-200'
                            }`}>
                                <div className="flex items-start gap-4">
                                    <div className={`flex-shrink-0 rounded-full p-3 ${
                                        balance.BALANCE < 0 ? 'bg-red-100' : 'bg-amber-100'
                                    }`}>
                                        <svg className={`w-6 h-6 ${
                                            balance.BALANCE < 0 ? 'text-red-600' : 'text-amber-600'
                                        }`} fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div className="flex-1">
                                        <h4 className={`text-lg font-bold mb-2 ${
                                            balance.BALANCE < 0 ? 'text-red-900' : 'text-amber-900'
                                        }`}>
                                            {balance.BALANCE < 0 ? 'Outstanding Balance' : 'Credit Balance'}
                                        </h4>
                                        <p className={`text-sm ${
                                            balance.BALANCE < 0 ? 'text-red-800' : 'text-amber-800'
                                        }`}>
                                            {balance.BALANCE < 0 
                                                ? `You have an outstanding balance of ${formatCurrency(Math.abs(balance.BALANCE))}.`
                                                : `You have a credit of ${formatCurrency(balance.BALANCE)}. This will be refunded or applied to future purchases.`
                                            }
                                        </p>
                                        {balance.BALANCE < 0 && (
                                            <button
                                                onClick={openPaymentModal}
                                                className="mt-4 bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-semibold transition-all shadow-md hover:shadow-lg"
                                            >
                                                Make a Payment
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Fully Paid Success Message */}
                        {balance.BALANCE === 0 && (
                            <div className="bg-green-50 border-2 border-green-200 rounded-2xl shadow-lg p-6">
                                <div className="flex items-start gap-4">
                                    <div className="flex-shrink-0 rounded-full bg-green-100 p-3">
                                        <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="text-lg font-bold text-green-900 mb-2">All Paid Up!</h4>
                                        <p className="text-sm text-green-800">
                                            Your tournament balance is fully paid. You're all set!
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
                        <svg className="w-20 h-20 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">No Balance Information</h2>
                        <p className="text-gray-600">No financial data available for this tournament</p>
                    </div>
                )}
            </div>

            {/* Payment Modal */}
            {showPaymentModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
                    <div className="min-h-screen px-4 py-8 flex items-center justify-center">
                        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full my-8">
                        {/* Modal Header */}
                        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-2xl">
                            <div className="flex items-center justify-between">
                                <h2 className="text-2xl font-bold">Make a Payment</h2>
                                <button
                                    onClick={closePaymentModal}
                                    disabled={isProcessingPayment}
                                    className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6 space-y-6">
                            {/* Payment Amount Input */}
                            <div>
                                <label className="block text-gray-700 font-semibold mb-2">
                                    Payment Amount
                                </label>
                                <div className="relative">
                                    <span className="absolute left-4 top-3 text-gray-600 text-lg font-bold">$</span>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0.01"
                                        value={paymentAmount}
                                        onChange={(e) => setPaymentAmount(e.target.value)}
                                        disabled={isProcessingPayment}
                                        placeholder="0.00"
                                        className="w-full pl-8 pr-4 py-3 border-2 border-gray-300 rounded-lg text-lg font-semibold focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                                    />
                                </div>
                                <p className="mt-2 text-sm text-gray-600">
                                    Outstanding balance: {balance ? formatCurrency(Math.abs(balance.BALANCE)) : '$0.00'}
                                </p>
                            </div>

                            {/* Fee Breakdown */}
                            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">Payment Amount:</span>
                                    <span className="font-semibold">${parseFloat(paymentAmount || '0').toFixed(2)}</span>
                                </div>
                                {isGermanTournament && (
                                    <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-md p-2">
                                        <svg className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                        </svg>
                                        <p className="text-xs text-amber-800">German Sales Tax is already included in the package price.</p>
                                    </div>
                                )}
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">CC Processing Fee ({isGermanTournament ? '3' : '5'}%):</span>
                                    <span className="font-semibold text-blue-600">${calculateFee().toFixed(2)}</span>
                                </div>
                                <div className="border-t border-gray-300 pt-2 flex justify-between">
                                    <span className="font-bold text-gray-900">Total Charge:</span>
                                    <span className="font-bold text-lg text-blue-600">${calculateTotal().toFixed(2)}</span>
                                </div>
                            </div>

                            {/* Card Information */}
                            <div className="space-y-4">
                                <h3 className="font-semibold text-gray-900">Card Information</h3>
                                
                                {/* Card Number */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Card Number
                                    </label>
                                    <div id="card-number" className="border-2 border-gray-300 rounded-lg p-3 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500 transition"></div>
                                </div>

                                {/* Expiration and CVV */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Expiration
                                        </label>
                                        <div id="card-date" className="border-2 border-gray-300 rounded-lg p-3 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500 transition"></div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            CVV
                                        </label>
                                        <div id="card-cvv" className="border-2 border-gray-300 rounded-lg p-3 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500 transition"></div>
                                    </div>
                                </div>

                                {/* Postal Code */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Postal Code
                                    </label>
                                    <div id="card-postal-code" className="border-2 border-gray-300 rounded-lg p-3 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500 transition"></div>
                                </div>
                            </div>

                            {/* Error Message */}
                            {paymentError && (
                                <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
                                    <div className="flex items-start gap-3">
                                        <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                        </svg>
                                        <p className="text-sm text-red-800 font-medium">{paymentError}</p>
                                    </div>
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div className="flex gap-3">
                                <button
                                    onClick={closePaymentModal}
                                    disabled={isProcessingPayment}
                                    className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handlePayment}
                                    disabled={isProcessingPayment || !cloverLoaded || !paymentAmount || parseFloat(paymentAmount) <= 0}
                                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {isProcessingPayment ? (
                                        <>
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                            <span>Processing...</span>
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            <span>Pay ${calculateTotal().toFixed(2)}</span>
                                        </>
                                    )}
                                </button>
                            </div>

                            {/* Security Notice */}
                            <div className="text-center">
                                <p className="text-xs text-gray-500">
                                    <svg className="w-4 h-4 inline mr-1" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                    </svg>
                                    Secure payment powered by Clover
                                </p>
                            </div>
                        </div>
                    </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PlayerFinances;
